'use client';

/**
 * ProcessesSection — the consolidated "how inputs become outputs" block.
 * Stacks four sub-sections: Resolver, Scoring, Audit, Budget. All numbers
 * come from the live ArchitectureState payload. No fetches of its own.
 */

import type { ArchitectureState } from '@/lib/architecture-state';

const PILLAR_ORDER = ['Profitability', 'Growth', 'Safety', 'ShareholderReturns', 'Valuation'] as const;
const PILLAR_SHORT: Record<string, string> = {
  Profitability: 'Profit',
  Growth: 'Growth',
  Safety: 'Safety',
  ShareholderReturns: 'SR',
  Valuation: 'Value',
};

function fmtCount(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

function fmtTime(ms: number | null): string {
  if (ms == null) return '—';
  return new Date(ms).toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function budgetTone(pct: number): string {
  if (pct >= 0.95) return 'text-red-300';
  if (pct >= 0.8) return 'text-amber-300';
  return 'text-emerald-300';
}

function budgetBar(pct: number): string {
  if (pct >= 0.95) return 'bg-red-400';
  if (pct >= 0.8) return 'bg-amber-300';
  return 'bg-emerald-400';
}

function pillarFill(pct: number): string {
  if (pct <= 0) return 'bg-zinc-700';
  if (pct >= 0.95) return 'bg-emerald-400';
  if (pct >= 0.8) return 'bg-amber-300';
  return 'bg-red-400';
}

interface ProvenanceBarProps {
  label: string;
  pct: number;
  color: string;
}

function ProvenanceBar({ label, pct, color }: ProvenanceBarProps): React.JSX.Element {
  return (
    <div>
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-white/70">{label}</span>
        <span className="tabular-nums text-white/90">{(pct * 100).toFixed(1)}%</span>
      </div>
      <div className="mt-0.5 h-1 rounded-full bg-zinc-800 overflow-hidden">
        <div
          className="h-full transition-[width] duration-500 ease-out"
          style={{ width: `${(pct * 100).toFixed(1)}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

interface Props {
  state: ArchitectureState;
}

export function ProcessesSection({ state }: Props): React.JSX.Element {
  const { resolver, scoring, pillars, budget, audit } = state;

  // worst factor from factorTrend (highest missing %)
  const worst = state.factorTrend.length > 0 ? state.factorTrend[0] : null;

  return (
    <div className="rounded-md border border-zinc-800 bg-[#0a0e14] p-4">
      <div className="flex items-baseline justify-between">
        <div className="text-[12px] uppercase tracking-wider text-[#c9a84c]">
          Processes
        </div>
        <div className="text-[10px] text-white/40">how inputs become outputs</div>
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* RESOLVER */}
        <div className="rounded border border-zinc-800/70 bg-zinc-950/40 p-3">
          <div className="flex items-baseline justify-between">
            <div className="text-[10px] uppercase tracking-wider text-white/70">Resolver</div>
            <div className="text-[9px] text-white/40 tabular-nums">
              {fmtCount(resolver.fieldCount)} cells
            </div>
          </div>
          <div className="mt-2 space-y-1.5">
            <ProvenanceBar label="BBG" pct={resolver.bbgPct} color="#34d399" />
            <ProvenanceBar label="FMP" pct={resolver.fmpPct} color="#60a5fa" />
            <ProvenanceBar label="Derived" pct={resolver.derivedPct} color="#fbbf24" />
            <ProvenanceBar label="Null" pct={resolver.nullPct} color="#f87171" />
          </div>
        </div>

        {/* SCORING */}
        <div className="rounded border border-zinc-800/70 bg-zinc-950/40 p-3">
          <div className="flex items-baseline justify-between">
            <div className="text-[10px] uppercase tracking-wider text-white/70">Scoring</div>
            <div className="text-[9px] text-white/40 tabular-nums">
              {scoring.lastWriteMs ? `scores.json @ ${fmtTime(scoring.lastWriteMs)}` : '—'}
            </div>
          </div>
          <div className="mt-2 text-[11px] text-white/85">
            <span className="tabular-nums font-semibold">{fmtCount(scoring.stocksScored)}</span>
            <span className="text-white/50"> stocks · </span>
            <span className="tabular-nums font-semibold">{scoring.factorCount}</span>
            <span className="text-white/50"> factors</span>
          </div>
          <div className="mt-3 grid grid-cols-5 gap-1">
            {PILLAR_ORDER.map((p) => {
              const pct = pillars[p] ?? 0;
              return (
                <div key={p} className="flex flex-col items-center gap-0.5">
                  <div className="text-[9px] tabular-nums text-white/80">
                    {(pct * 100).toFixed(0)}%
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                    <div
                      className={`h-full ${pillarFill(pct)}`}
                      style={{ width: `${(pct * 100).toFixed(1)}%` }}
                    />
                  </div>
                  <div className="text-[8px] text-white/50 uppercase tracking-wider">
                    {PILLAR_SHORT[p] ?? p}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* AUDIT */}
        <div className="rounded border border-zinc-800/70 bg-zinc-950/40 p-3">
          <div className="flex items-baseline justify-between">
            <div className="text-[10px] uppercase tracking-wider text-white/70">Audit</div>
            <div className="text-[9px] text-white/40">factor missing %</div>
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <span
              className={`text-[20px] font-semibold tabular-nums ${
                audit?.todayMissingPct != null && audit.todayMissingPct > 5
                  ? 'text-red-300'
                  : audit?.todayMissingPct != null && audit.todayMissingPct > 3
                    ? 'text-amber-300'
                    : 'text-emerald-300'
              }`}
            >
              {audit?.todayMissingPct != null ? `${audit.todayMissingPct.toFixed(1)}%` : '—'}
            </span>
            <span className="text-[10px] text-white/40">today</span>
          </div>
          {worst && (
            <div className="mt-2 text-[10px] text-white/60">
              <span className="text-white/40 uppercase tracking-wider">worst factor: </span>
              <span className="text-white/85 font-mono">{worst.factor}</span>
              <span
                className={`ml-1.5 tabular-nums ${
                  worst.todayMissingPct > 10 ? 'text-red-300' : 'text-amber-300'
                }`}
              >
                {worst.todayMissingPct.toFixed(1)}%
              </span>
              <span className="ml-1 text-white/40">missing</span>
            </div>
          )}
          {audit?.fiveDayHistory && audit.fiveDayHistory.length > 0 && (
            <div className="mt-2 flex items-center gap-1.5">
              <span className="text-[9px] uppercase tracking-wider text-white/40">5-day</span>
              {audit.fiveDayHistory.map((h, i) => (
                <span
                  key={i}
                  title={`${h.date}: ${h.missingPct == null ? '—' : h.missingPct.toFixed(1) + '%'}`}
                  className={`inline-block w-2 h-2 rounded-full ${
                    h.missingPct == null
                      ? 'bg-zinc-700'
                      : h.missingPct > 5
                        ? 'bg-red-400'
                        : h.missingPct > 3
                          ? 'bg-amber-300'
                          : 'bg-emerald-400'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* BUDGET */}
        <div className="rounded border border-zinc-800/70 bg-zinc-950/40 p-3">
          <div className="flex items-baseline justify-between">
            <div className="text-[10px] uppercase tracking-wider text-white/70">Budget</div>
            <div className="text-[9px] text-white/40">daily caps</div>
          </div>
          <div className="mt-2 space-y-2">
            <BudgetRow label="SAPI" used={budget.sapi.used} cap={budget.sapi.cap} pct={budget.sapi.pct} />
            <BudgetRow label="FMP" used={budget.fmp.used} cap={budget.fmp.cap} pct={budget.fmp.pct} />
          </div>
        </div>
      </div>
    </div>
  );
}

interface BudgetRowProps {
  label: string;
  used: number;
  cap: number;
  pct: number;
}

function BudgetRow({ label, used, cap, pct }: BudgetRowProps): React.JSX.Element {
  const widthPct = Math.min(100, pct * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="w-12 text-[9px] uppercase tracking-wider text-white/60">{label}</div>
      <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <div
          className={`h-full ${budgetBar(pct)} transition-[width] duration-500 ease-out`}
          style={{ width: `${widthPct.toFixed(1)}%` }}
        />
      </div>
      <div className="w-28 text-right text-[9px] tabular-nums text-white/70">
        {fmtCount(used)}/{fmtCount(cap)}
      </div>
      <div className={`w-10 text-right text-[10px] tabular-nums font-semibold ${budgetTone(pct)}`}>
        {(pct * 100).toFixed(0)}%
      </div>
    </div>
  );
}
